import React from "react";
import { Modal, Empty, Icon } from "antd";
import PropTypes from "prop-types";
import { formatDateStr, parseJSON } from "../utils/formatters";
import { actionConfig } from "../utils/constants";

/**
 * ActivityLogsModal - Modal showing wallet activity history with filtering
 */
const ActivityLogsModal = ({
  visible,
  logs,
  loading,
  expandedLogIds,
  filterWallet,
  filterAction,
  onFilterWalletChange,
  onFilterActionChange,
  onToggleExpand,
  onWalletClick,
  onCancel,
}) => {
  // Get unique wallet IDs and actions for filter
  const uniqueWallets = [...new Set(logs.map((l) => l.wallet_id))].sort(
    (a, b) => a - b
  );
  const uniqueActions = [...new Set(logs.map((l) => l.action))];

  // Apply filters
  const filteredLogs = logs.filter((log) => {
    const walletMatch =
      filterWallet === "all" || String(log.wallet_id) === String(filterWallet);
    const actionMatch = filterAction === "all" || log.action === filterAction;
    return walletMatch && actionMatch;
  });

  const renderChangeDetails = (log) => {
    const oldVal = parseJSON(log.old_value);
    const newVal = parseJSON(log.new_value);
    if (!oldVal && !newVal) return null;

    const changes = [];
    const allKeys = new Set([
      ...Object.keys(oldVal || {}),
      ...Object.keys(newVal || {}),
    ]);

    allKeys.forEach((key) => {
      const oldV = oldVal ? oldVal[key] : undefined;
      const newV = newVal ? newVal[key] : undefined;
      if (JSON.stringify(oldV) !== JSON.stringify(newV)) {
        changes.push({ key, oldV, newV });
      }
    });

    if (changes.length === 0) return null;

    return (
      <div className="activity-log-changes">
        {changes.map((c, i) => (
          <div key={i} className="activity-log-change-item">
            <span className="change-key">{c.key.replace(/_/g, " ")}:</span>
            {c.oldV !== undefined && (
              <span className="change-old">{String(c.oldV)}</span>
            )}
            {c.oldV !== undefined && c.newV !== undefined && (
              <Icon type="arrow-right" className="change-arrow" />
            )}
            {c.newV !== undefined && (
              <span className="change-new">{String(c.newV)}</span>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <Modal
      title={
        <div className="activity-modal-header-premium">
          <div className="activity-modal-icon-wrap">
            <Icon type="history" />
          </div>
          <div className="activity-modal-title-group">
            <span className="activity-modal-title-main">
              Wallet Activity History
            </span>
            <span className="activity-modal-title-sub">
              {filteredLogs.length} of {logs.length} activities
            </span>
          </div>
        </div>
      }
      visible={visible}
      onCancel={onCancel}
      footer={null}
      width={680}
      className="activity-logs-modal-premium"
      maskClosable={false}
    >
      {loading ? (
        <div className="activity-loading-premium">
          <div className="activity-loading-spinner" />
          <div className="activity-loading-text">
            Loading activity history...
          </div>
        </div>
      ) : logs.length === 0 ? (
        <Empty description="No activity logs found" />
      ) : (
        <React.Fragment>
          {/* Filter Bar */}
          <div className="activity-filter-bar">
            <div className="activity-filter-item">
              <label>Wallet</label>
              <select
                value={filterWallet}
                onChange={(e) => onFilterWalletChange(e.target.value)}
              >
                <option value="all">All Wallets</option>
                {uniqueWallets.map((w) => (
                  <option key={w} value={w}>
                    Wallet #{w}
                  </option>
                ))}
              </select>
            </div>
            <div className="activity-filter-item">
              <label>Action</label>
              <select
                value={filterAction}
                onChange={(e) => onFilterActionChange(e.target.value)}
              >
                <option value="all">All Actions</option>
                {uniqueActions.map((a) => (
                  <option key={a} value={a}>
                    {actionConfig[a] ? actionConfig[a].label : a}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Timeline */}
          <div className="activity-timeline-premium">
            {filteredLogs.length === 0 ? (
              <Empty
                description="No matching activities"
                style={{ padding: 40 }}
              />
            ) : (
              filteredLogs.map((log, index) => {
                const config = actionConfig[log.action] || {
                  icon: "question",
                  color: "#8c8c8c",
                  label: log.action,
                  bg: "#f5f5f5",
                };
                const isExpanded = expandedLogIds.includes(log.id);
                const isLast = index === filteredLogs.length - 1;

                return (
                  <div key={log.id} className="activity-card-premium">
                    {/* Connector Line */}
                    {!isLast && (
                      <div
                        className="activity-connector"
                        style={{
                          background: `linear-gradient(180deg, ${config.color}40, transparent)`,
                        }}
                      />
                    )}

                    {/* Action Badge */}
                    <div
                      className="activity-badge"
                      style={{
                        background: `linear-gradient(135deg, ${config.color}, ${config.color}cc)`,
                      }}
                    >
                      <Icon type={config.icon} />
                    </div>

                    {/* Card Content */}
                    <div
                      className="activity-card-content"
                      style={{ borderLeftColor: config.color }}
                    >
                      {/* Card Header */}
                      <div className="activity-card-header">
                        <div className="activity-card-title-row">
                          <span
                            className="activity-action-label"
                            style={{ color: config.color }}
                          >
                            {config.label}
                          </span>
                          <span
                            className="activity-wallet-badge activity-wallet-badge-clickable"
                            onClick={(e) => {
                              e.stopPropagation();
                              onWalletClick(log.wallet_id);
                            }}
                          >
                            <Icon
                              type="wallet"
                              style={{ marginRight: 4, fontSize: 10 }}
                            />
                            Wallet #{log.wallet_id}
                          </span>
                        </div>
                        <div className="activity-meta-row">
                          <span className="activity-timestamp">
                            <Icon
                              type="clock-circle"
                              style={{ marginRight: 4, fontSize: 10 }}
                            />
                            {formatDateStr(log.created_date && log.created_date.Time)}
                          </span>
                          {log.admin_name && log.admin_name.Valid && log.admin_name.String && (
                            <span className="activity-admin-name">
                              <Icon
                                type="user"
                                style={{ marginRight: 4, fontSize: 10 }}
                              />
                              {log.admin_name.String.trim()}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Note Section */}
                      {log.note && (
                        <div className="activity-note-premium">
                          <Icon type="message" />
                          <span>{log.note}</span>
                        </div>
                      )}

                      {/* Expand Button */}
                      {(log.old_value || log.new_value) && (
                        <button
                          className="activity-expand-btn-premium"
                          onClick={() => onToggleExpand(log.id)}
                        >
                          <Icon type={isExpanded ? "up" : "down"} />
                          <span>Details</span>
                        </button>
                      )}

                      {/* Change Details */}
                      {isExpanded && (
                        <div className="activity-changes-premium">
                          {renderChangeDetails(log)}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </React.Fragment>
      )}
    </Modal>
  );
};

ActivityLogsModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  logs: PropTypes.array,
  loading: PropTypes.bool,
  expandedLogIds: PropTypes.array,
  filterWallet: PropTypes.string,
  filterAction: PropTypes.string,
  onFilterWalletChange: PropTypes.func.isRequired,
  onFilterActionChange: PropTypes.func.isRequired,
  onToggleExpand: PropTypes.func.isRequired,
  onWalletClick: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

ActivityLogsModal.defaultProps = {
  logs: [],
  loading: false,
  expandedLogIds: [],
  filterWallet: "all",
  filterAction: "all",
};

export default ActivityLogsModal;



// WEBPACK FOOTER //
// ./src/containers/User/UserManagement/components/MemberCreditWallet/modals/ActivityLogsModal.js